import { useEffect, useState } from 'react';
import { cx } from 'class-variance-authority';
import { Reorder } from 'motion/react';
import { OnboardingTabConfig } from '@/lib/dtos';
import { ONBOARDING_ICON_CATALOG } from '@/lib/onboarding-icon-catalog';

export interface MockupTabBarProps {
  tabs: OnboardingTabConfig[];
  accentColor: string;
  /** Index of the visually active tab (defaults to the first tab). */
  activeIndex?: number;
  /**
   * Called with a tab's index when it's tapped. Optional and unused by
   * read-only previews (e.g. NotificationPreviewPage) — only
   * TabBarConfigPage wires this up, to jump straight to that tab's editor.
   */
  onTabClick?: (index: number) => void;
  /**
   * Called with the full tab list, reordered, once a drag-to-reorder
   * gesture on a tab settles. Optional and unused by read-only previews —
   * only TabBarConfigPage wires this up, letting the merchant drag tabs
   * directly on the phone instead of only in the form list beside it.
   * Picking a tab up here is press-and-drag on the tab itself (there's no
   * room in a 60px-tall bar for a separate handle), same as the form list's
   * rows — a plain tap without meaningful movement still reaches
   * `onTabClick` underneath, since Framer Motion only treats a gesture as a
   * drag once it crosses its own movement threshold.
   */
  onReorder?: (tabs: OnboardingTabConfig[]) => void;
}

const MAX_VISIBLE_TABS = 5;

// Renders the configured tab bar preview from the lead's tabs config,
// coloring the active item with `accentColor`. Visually caps at 5 tabs,
// matching a typical iOS tab bar (edge case in spec.md) — also exactly
// MAX_TABS, so `onReorder`'s full-array callback never has to account for
// tabs truncated out of view here.
export function MockupTabBar({
  tabs,
  accentColor,
  activeIndex = 0,
  onTabClick,
  onReorder,
}: MockupTabBarProps) {
  const visibleTabs = tabs.slice(0, MAX_VISIBLE_TABS);
  const rowClassName =
    'flex h-[60px] w-full items-stretch justify-around border-t border-[#eee] px-1';

  return (
    <div className="w-full bg-white">
      {onReorder ? (
        <Reorder.Group
          as="div"
          axis="x"
          values={visibleTabs}
          onReorder={onReorder}
          className={rowClassName}
        >
          {visibleTabs.map((tab, index) => (
            <TabBarButton
              key={tab.key}
              tab={tab}
              index={index}
              isActive={index === activeIndex}
              accentColor={accentColor}
              onTabClick={onTabClick}
              draggable
            />
          ))}
        </Reorder.Group>
      ) : (
        <div className={rowClassName}>
          {visibleTabs.map((tab, index) => (
            <TabBarButton
              key={tab.key}
              tab={tab}
              index={index}
              isActive={index === activeIndex}
              accentColor={accentColor}
              onTabClick={onTabClick}
            />
          ))}
        </div>
      )}
      {/* Safe-area clearance for the home-indicator overlay (IPhoneMockup),
          mirroring how the real app extends its screen background below the
          tab bar rather than padding the tab bar itself. */}
      <div className="h-6 w-full" />
    </div>
  );
}

interface TabBarButtonProps {
  tab: OnboardingTabConfig;
  index: number;
  isActive: boolean;
  accentColor: string;
  onTabClick?: (index: number) => void;
  draggable?: boolean;
}

// Icon + label only — shared between the draggable (`Reorder.Item`) and
// static (`button`) renders below, which otherwise need different host
// elements (`Reorder.Item` requires a `Reorder.Group` ancestor, so it can't
// just be this same component always-rendered with drag conditionally
// disabled).
function TabBarButtonContent({
  tab,
  color,
}: {
  tab: OnboardingTabConfig;
  color: string;
}) {
  const catalogEntry = ONBOARDING_ICON_CATALOG.find(
    (entry) => entry.key === tab.iconKey
  );

  return (
    <>
      {catalogEntry ? (
        <catalogEntry.Icon
          className="h-6 w-6"
          style={{ color }}
          aria-hidden="true"
        />
      ) : (
        <div className="h-6 w-6" />
      )}
      <span
        className="mt-1 max-w-[60px] truncate text-[10px]"
        style={{ color }}
      >
        {tab.title}
      </span>
    </>
  );
}

function TabBarButton({
  tab,
  index,
  isActive,
  accentColor,
  onTabClick,
  draggable,
}: TabBarButtonProps) {
  const color = isActive ? accentColor : '#7e7e7e';
  const ariaLabel = onTabClick
    ? `Editar a aba ${tab.title || index + 1}`
    : undefined;
  const className = cx(
    'flex flex-1 flex-col items-center justify-center',
    onTabClick && 'cursor-pointer transition-opacity hover:opacity-70',
    draggable && 'cursor-grab touch-none active:cursor-grabbing'
  );

  // Own state instead of `whileDrag`: this item's `scale` and the live
  // reorder's `layout` position animation both drive the same transform,
  // and `whileDrag`'s revert is only a heuristic around the gesture ending
  // — with `onReorder` re-rendering this list mid-drag (every time the
  // dragged tab crosses another one), that heuristic was observed to
  // occasionally miss, leaving the tab stuck "zoomed in" after release.
  // Driving `scale` off explicit state, flipped by the gesture's own
  // start/end callbacks, makes the revert unconditional instead.
  const [isDragging, setIsDragging] = useState(false);

  // Belt-and-suspenders alongside `onDragEnd`: if the pointer is released
  // (or the gesture is cancelled, e.g. an edge swipe or an OS-level
  // interruption) in a way the drag gesture itself doesn't cleanly end,
  // this still catches it and resets the scale.
  useEffect(() => {
    if (!isDragging) {
      return;
    }
    const reset = () => setIsDragging(false);
    window.addEventListener('pointerup', reset);
    window.addEventListener('pointercancel', reset);
    return () => {
      window.removeEventListener('pointerup', reset);
      window.removeEventListener('pointercancel', reset);
    };
  }, [isDragging]);

  if (draggable) {
    return (
      <Reorder.Item
        as="button"
        type="button"
        value={tab}
        disabled={!onTabClick}
        aria-label={ariaLabel}
        onClick={() => onTabClick?.(index)}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
        // `zIndex` while dragging is already handled internally by
        // `Reorder.Item` (derived from its own drag offset, overriding
        // anything passed in `style` here) — only `scale` needs our own
        // state-driven override, see above.
        animate={{ scale: isDragging ? 1.2 : 1 }}
        transition={{
          layout: { type: 'spring', bounce: 0.25, duration: 0.35 },
          scale: { duration: 0.15 },
        }}
        className={className}
      >
        <TabBarButtonContent tab={tab} color={color} />
      </Reorder.Item>
    );
  }

  return (
    <button
      type="button"
      disabled={!onTabClick}
      aria-label={ariaLabel}
      onClick={() => onTabClick?.(index)}
      className={className}
    >
      <TabBarButtonContent tab={tab} color={color} />
    </button>
  );
}
